import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Observable, of } from 'rxjs';
import * as SearchBoardActions from '@store/search-board/search-board.actions';
import {
  ChangeSidePanelTabView,
  ClearSelectedLayers,
  GetRegionCoordinatesFailure,
  GetRegionCoordinatesSuccess,
  SendEmail,
  UpdateMapPreferences
} from '@store/search-board/search-board.actions';
import { select, Store } from '@ngrx/store';
import { bufferTime, catchError, concatMap, delay, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { PictureViewerComponent } from '@root/search/components/picture-viewer/picture-viewer.component';
import { ModalType } from '@shared/models/modal.model';
import { SpinnerOverlayService } from '@shared/services/spinner-overlay.service';
import { ImageLoadService } from '@shared/services/image-load.service';
import { RibbonService } from '@shared/services/ribbon.service';
import { MapService } from '@root/search/services/map.service';
import { ActionName } from '@shared/models/effects-names.model';
import { RIBBON_TYPE } from '@shared/models/ribbon.model';
import {
  Disclaimer,
  Email,
  PointOfInterestConfig,
  PropertyData,
  PropertyInformation,
  UpdatePreferenceResponse
} from '@store/search-board/search-board.model';
import { AppState } from '@root/app-state.module';
import {
  selectIsMobile,
  selectPreferenceElement,
  selectPreferenceGroupByCode,
  selectPreferenceValue
} from '@store/user/user.selector';
import { PreferenceCode, PreferenceElement, PreferenceGroup } from '@store/user/user.model';
import { selectPropertyInformationById, selectSelectedPointsOfInterestProperty } from '@store/search-board/search-board.selector';
import { InstanetLinkResponse, LatLong, LayerInfo, ReverseLinkResponse } from '@root/search/models/map.models';
import { PropertySortModalComponent } from '@root/search/components/property-sort-modal/property-sort-modal.component';
import { selectedMapLayer, selectPropertyCharacteristic, selectTrends } from '@store/map-layers.selector';
import { PreferenceUtils } from '@shared/utils/preference-utils';
import { LayerId, PointOfInterestPreferenceValues } from '@root/search/models/map.constants';
import { EmailModalComponent } from '@shared/components/email-modal/email-modal.component';
import { HttpParams } from '@angular/common/http';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { DisclaimerModalComponent } from '@root/search/components/disclaimer-modal/disclaimer-modal.component';
import { PostcardsModalComponent } from '@shared/components/postcards-modal/postcards-modal.component';

const PREFERENCES_BUFFER_TIME = 7500;

@Injectable()
export class SearchBoardEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<AppState>,
    private readonly spinnerOverlayService: SpinnerOverlayService,
    private readonly imageLoadService: ImageLoadService,
    private readonly ribbonService: RibbonService,
    private readonly dialog: MatDialog,
    private readonly mapService: MapService,
    private readonly mixpanelService: MixpanelService) {
  }

  openPictureViewer$: Observable<SearchBoardActions.DownloadPropertyPhotosReport> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.OPEN_PICTURE_VIEWER),
      switchMap((action: SearchBoardActions.OpenPictureViewer) => {
        this.spinnerOverlayService.showSpinner(ActionName.LOAD_IMAGES_ON_PICTURE_VIEWER);
        return this.imageLoadService.getAllImageSources(action.payload.propertyData).pipe(
          map((imageSources) => [imageSources, action.payload.propertyData])
        );
      }),
      withLatestFrom(
        this.store$.pipe(select(selectIsMobile)),
        ([imageSource, propertyData], isMobile) => ([propertyData, imageSource, isMobile])
      ),
      delay(1000), // Needed this or the photo viewer would fail to render
      switchMap(([propertyData, imageSource, isMobile]: [PropertyData, Array<string>, boolean]) => {
        this.spinnerOverlayService.hideSpinner(ActionName.LOAD_IMAGES_ON_PICTURE_VIEWER);

        if(!!(imageSource && imageSource.length)){
          const dialogRef = this.dialog.open(PictureViewerComponent, {
            panelClass: isMobile ? ModalType.SCROLLABLE : ModalType.LARGE.concat(ModalType.HIDDEN),
            data: {
              propertyData: propertyData,
              imageSource,
              isMobile
            }
          });
  
          return dialogRef.afterClosed()
            .pipe(
              filter(Boolean),
              map(() => new SearchBoardActions.DownloadPropertyPhotosReport(propertyData))
            );
        } else {
          return of();
        }
      })
    );
  });

  downloadDisclaimer$: Observable<SearchBoardActions.DownloadDisclaimerSuccess> = createEffect(() => {
    return this.actions$.pipe(
        ofType(SearchBoardActions.DOWNLOAD_DISCLAIMER),
        switchMap(({ payload }: { payload: LatLong}) => {
          return this.mapService.loadDisclaimer(payload)
            .pipe(catchError(() => of(null)))
        }),
        filter(Boolean),
        map((data: {disclaimer: Disclaimer[], latLong: string }) => new SearchBoardActions.DownloadDisclaimerSuccess(data))
      );
    }
  );

  openDisclaimer$: Observable<any> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.OPEN_DISCLAIMER),
      switchMap(({ payload }) => {
        return this.dialog.open(DisclaimerModalComponent, { data: payload, panelClass: ModalType.SCROLLABLE }).afterClosed()
      })
    );
  }, {dispatch: false});

  loadPointOfInterestConfig$: Observable<SearchBoardActions.LoadMapPointOfInterestConfigSuccess> = createEffect(() => {
      return this.actions$.pipe(
        ofType(SearchBoardActions.LOAD_MAP_POINT_OF_INTEREST_CONFIG),
        switchMap(() => this.mapService.loadPointOfInterestConfig()
          .pipe(catchError(() => of(null)))),
        filter(Boolean),
        map((points: Array<PointOfInterestConfig>) => new SearchBoardActions.LoadMapPointOfInterestConfigSuccess(points))
      );
    }
  );

  getInstanetLink$: Observable<{}> = createEffect(() => {
      return this.actions$.pipe(
        ofType(SearchBoardActions.GET_INSTANET_LINK),
        this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.GetInstanetLink) => {
          return this.store$.pipe(
            take(1),
            select(selectPreferenceGroupByCode(PreferenceCode.PG_INSTANET_REVERSE_LINK)),
            switchMap((preferenceGroup: PreferenceGroup) => {
              const propertyInformation: PropertyInformation = {
                propertyData: action.payload,
                propertyIdentifier: null
              };
              return this.mapService.getInstanetLink(preferenceGroup, propertyInformation)
                .pipe(
                  map((link: InstanetLinkResponse) => link && link.instanetLinkURL || ''),
                  this.openLink('Link error or link does not exist')
                );
            })
          );
        }, ActionName.GET_PROPERTY_INFORMATION)
      );
    }, {dispatch: false}
  );

  getReverseLink$: Observable<string> = createEffect(() => {
      return this.actions$.pipe(
        ofType(SearchBoardActions.GET_REVERSE_LINK),
        this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.GetReverseLink) => {
          return this.store$.pipe(take(1), select(selectPreferenceGroupByCode(PreferenceCode.PG_RL_REVERSELINK)),
            withLatestFrom(this.store$.pipe(select(selectPropertyInformationById(action.payload)))),
            switchMap(([preferenceGroup, propertyInformation]: [PreferenceGroup, PropertyInformation]) => {
              return this.getReverseLink(preferenceGroup, propertyInformation);
            }));
        }, ActionName.GET_PROPERTY_INFORMATION)
      );
    }, {dispatch: false}
  );

  getPropertyReverseLink$: Observable<string> = createEffect(() => {
      return this.actions$.pipe(
        ofType(SearchBoardActions.GET_PROPERTY_REVERSE_LINK),
        this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.GetPropertyReverseLink) => {
          return this.store$.pipe(take(1), select(selectPreferenceGroupByCode(PreferenceCode.PG_RL_REVERSELINK)),
            switchMap((preferenceGroup: PreferenceGroup) => {
              const propertyInformation: PropertyInformation = {
                propertyData: action.payload,
                propertyIdentifier: null
              };
              return this.getReverseLink(preferenceGroup, propertyInformation);
            }));
        }, ActionName.GET_PROPERTY_INFORMATION)
      );
    }, {dispatch: false}
  );

  getReverseLinkGrid$: Observable<string> = createEffect(() => {
      return this.actions$.pipe(
        ofType(SearchBoardActions.GET_REVERSE_LINK_GRID),
        this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.GetReverseLinkGrid) => {
          return this.store$.pipe(take(1), select(selectPreferenceGroupByCode(PreferenceCode.PG_RL_REVERSELINK)),
            withLatestFrom(
              action.payload !== null && typeof action.payload === 'object'
                ? of(action.payload)
                : this.store$.pipe(select(selectPropertyInformationById(action.payload as unknown as string)))
            ),
            switchMap(([preferenceGroup, propertyInformation]: [PreferenceGroup, PropertyInformation]) => {
              return this.getReverseLink(preferenceGroup, propertyInformation);
            }));
        }, ActionName.GET_PROPERTY_INFORMATION)
      );
    }, {dispatch: false}
  );

  getRegionCoordinates$: Observable<GetRegionCoordinatesSuccess | GetRegionCoordinatesFailure> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.GET_REGION_GEOCODES),
      filter((action: SearchBoardActions.GetRegionCoordinates) => {
        return !action.payload.some((preferenceGroup: PreferenceGroup) => {
          return preferenceGroup.prefGroupCode === PreferenceCode.PG_RL_GENERAL_PREFERENCES &&
            !!PreferenceUtils.getPreferenceElementValue(preferenceGroup, PreferenceCode.PG_GENPREF_MAP_BOUNDS);
        });
      }),
      switchMap(() => {
        return this.mapService.getRegionCoordinates().pipe(
          catchError(() => of(null)),
          map((result) => result ? new GetRegionCoordinatesSuccess(result) : new GetRegionCoordinatesFailure()));
      })
    );
  });

  openSortModal$: Observable<any> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.OPEN_SORT_MODAL),
      switchMap(() => this.dialog.open(PropertySortModalComponent, {
        panelClass: ModalType.SCROLLABLE
      }).afterClosed())
    );
  }, {dispatch: false});

  openPostcardsModal: Observable<void> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.OPEN_POSTCARDS_MODAL),
      switchMap((action: SearchBoardActions.OpenPostcardsModal) => this.dialog.open(PostcardsModalComponent, {
        panelClass: [...ModalType.SCROLLABLE, 'postcards-modal'],
        data: {
          propertyInformation: action.payload,
        }
      }).afterClosed())
    )
  }, { dispatch: false });

  saveClearedLayers$: Observable<UpdateMapPreferences> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.CLEAR_SELECTED_LAYERS),
      map((action: ClearSelectedLayers) => action.payload),
      withLatestFrom(
        this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_MAP_BOUNDARIES))),
        this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_MAP_DISTRESSED_PROPERTIES))),
        this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_MAP_LAYERS_LOCATION_INFO))),
        this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_MAP_LAYERS)))
      ),
      map(([selectedLayers, ...preferenceGroups]) => {
        const preferences = preferenceGroups.reduce((preferenceElements: Array<PreferenceElement>, preferenceGroup: PreferenceGroup) => {
          selectedLayers.forEach((layerInfo: LayerInfo) => {
            const preferenceElement = PreferenceUtils.getPreferenceElement(preferenceGroup, layerInfo.preferenceElementCode);
            if (preferenceElement) {
              preferenceElements.push(PreferenceUtils.setPreferenceValue(preferenceElement, !layerInfo.isSelected));
            }
          });

          return preferenceElements;
        }, [] as Array<PreferenceElement>);

        return new UpdateMapPreferences(preferences);
      })
    );
  });

  setBoundaries$: Observable<UpdateMapPreferences> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.TOGGLE_BOUNDARY),
      withLatestFrom(
        this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_MAP_BOUNDARIES)))
      ),
      this.sendBufferedLayers()
    );
  });

  setDistressedProperties$: Observable<UpdateMapPreferences> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.TOGGLE_DISTRESSED_PROPERTY),
      withLatestFrom(
        this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_MAP_DISTRESSED_PROPERTIES)))
      ),
      this.sendBufferedLayers()
    );
  });

  setPropertySalesLayer$: Observable<UpdateMapPreferences> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.TOGGLE_SALES_VALUE, SearchBoardActions.TOGGLE_MLS_LISTING),
      withLatestFrom(
        this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_MAP_LAYERS)))
      ),
      this.sendBufferedLayers()
    );
  });

  setPropertyCharacteristic$: Observable<UpdateMapPreferences> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.SET_PROPERTY_CHARACTERISTIC, SearchBoardActions.CLEAR_SELECTED_LAYERS),
      withLatestFrom(
        this.store$.pipe(
          select(selectPreferenceElement(PreferenceCode.PG_MAP_LAYERS, PreferenceCode.PREF_MAP_LAYERS_PROPERTY_CHARACTERISTICS))
        ),
        this.store$.pipe(select(selectPropertyCharacteristic))
      ),
      map(([_, propertyCharacteristicElement, selectedPropertyCharacteristic]) => {
        return new UpdateMapPreferences([
          PreferenceUtils.setPreferenceValue(propertyCharacteristicElement, selectedPropertyCharacteristic.layerId)
        ]);
      })
    );
  });

  setPremiumLayer$: Observable<UpdateMapPreferences> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.SET_PREMIUM_LAYER, SearchBoardActions.CLEAR_SELECTED_LAYERS),
      withLatestFrom(
        this.store$.pipe(
          select(selectPreferenceElement(PreferenceCode.PG_MAP_TOOLS, PreferenceCode.PREF_PREMIUM_MAP_LAYERS))
        ),
        this.store$.pipe(select(selectedMapLayer))
      ),
      map(([_, propertyCharacteristicElement, selectedMapLayerElement]) => {
        return new UpdateMapPreferences([
          PreferenceUtils
            .setPreferenceValue(
              propertyCharacteristicElement,
              selectedMapLayerElement && selectedMapLayerElement.length ? selectedMapLayerElement[0].layerId : LayerId.SELECT_NONE
            )
        ]);
      })
    );
  });

  setLocationInformation$: Observable<UpdateMapPreferences> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.TOGGLE_LOCATION_INFORMATION),
      withLatestFrom(
        this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_MAP_LAYERS_LOCATION_INFO)))
      ),
      this.sendBufferedLayers()
    );
  });

  setPointOfInterest$: Observable<UpdateMapPreferences> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        SearchBoardActions.ADD_POINT_OF_INTEREST_PROPERTY,
        SearchBoardActions.REMOVE_POINT_OF_INTEREST_PROPERTY,
        SearchBoardActions.CLEAR_SELECTED_LAYERS
      ),
      withLatestFrom(
        this.store$.pipe(select(selectPreferenceElement(PreferenceCode.PG_MAP_TOOLS, PreferenceCode.PREF_MAP_POINTS_OF_INTEREST))),
        this.store$.pipe(select(selectSelectedPointsOfInterestProperty))
      ),
      map(([_, pointOfInterestElement, selectedPointsOfInterest]) => {
        const value = selectedPointsOfInterest.map((pointOfInterest: string) => PointOfInterestPreferenceValues[pointOfInterest]).join(' ');
        return new UpdateMapPreferences([
          PreferenceUtils.setPreferenceValue(pointOfInterestElement, value)
        ]);
      })
    );
  });

  updateMapPreferences$: Observable<UpdatePreferenceResponse> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.UPDATE_MAP_PREFERENCES),
      map((action: UpdateMapPreferences) => action.payload),
      bufferTime(PREFERENCES_BUFFER_TIME),
      filter((buffer: Array<Array<PreferenceElement>>) => buffer.length > 0),
      map((buffer: Array<Array<PreferenceElement>>) => {
        return buffer.reduce((result: { [key: string]: PreferenceElement }, values: Array<PreferenceElement>) => {
          values.forEach((preferenceElement: PreferenceElement) => {
            result[preferenceElement.prefCode] = preferenceElement;
          });
          return result;
        }, {});
      }),
      concatMap((buffer: { [key: string]: PreferenceElement }) => {
        const preferences = Object.values
          ? Object.values(buffer)
          : Object.keys(buffer).map((key) => buffer[key]);
        return this.mapService.updateMapPreferences(preferences).pipe(catchError(() => of(null)));
      })
    );
  }, {dispatch: false});

  setTrendsAggregationLevel$: Observable<UpdateMapPreferences> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        SearchBoardActions.SET_TRENDS_AGGREGATION_LEVEL,
        SearchBoardActions.SET_TRENDS_LAYER,
        SearchBoardActions.SET_TRENDS_PROPERTY_TYPE,
        SearchBoardActions.SET_TRENDS_OPACITY,
        SearchBoardActions.CLEAR_SELECTED_LAYERS
      ),
      withLatestFrom(
        this.store$.pipe(select(selectPreferenceElement(PreferenceCode.PG_MAP_TRENDS_LAYER, PreferenceCode.PREF_MAP_TENDS_LAYER))),
        this.store$.pipe(select(selectPreferenceElement(PreferenceCode.PG_MAP_TRENDS_LAYER, PreferenceCode.PREF_MAP_AGGREGATION))),
        this.store$.pipe(select(selectPreferenceElement(PreferenceCode.PG_MAP_TRENDS_LAYER, PreferenceCode.PREF_MAP_TRENDS_PROPERTY_TYPE))),
        this.store$.pipe(select(selectPreferenceElement(PreferenceCode.PG_MAP_TRENDS_LAYER, PreferenceCode.PREF_MAP_TRENDS_OPACITY))),
        this.store$.pipe(select(selectTrends))
      ),
      map(([_, trendsLayer, aggregation, propertyType, opacity, trendValues]) => {
        return new UpdateMapPreferences([
          PreferenceUtils.setPreferenceValue(trendsLayer, trendValues.selectedTrendsLayer.layerId),
          PreferenceUtils.setPreferenceValue(aggregation, trendValues.aggregationLevel),
          PreferenceUtils.setPreferenceValue(propertyType, trendValues.propertyType),
          PreferenceUtils.setPreferenceValue(opacity, trendValues.opacity)
        ]);
      })
    );
  });

  sendEmailDriveDirections$: Observable<SendEmail> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.SEND_EMAIL_DRIVE_DIRECTION),
      withLatestFrom(this.store$.pipe(
        select(selectPreferenceValue(PreferenceCode.PG_RL_GENERAL_PREFERENCES, PreferenceCode.PG_GENPREF_USER_EMAIL)))),
      switchMap(([action, replyTo]: [SearchBoardActions.SendEmailDriveDirection, string]) => {
        const drivingDirectionsParams = new HttpParams({
          fromObject: {
            api: '1',
            travelmode: 'driving',
            origin: action.payload.origin,
            destination: action.payload.destination
          }
        });
        const drivingDirectionsUrl = `https://www.google.com/maps/dir/?${drivingDirectionsParams.toString()}`;
        return this.dialog
          .open(
            EmailModalComponent, {
              panelClass: ModalType.SCROLLABLE.concat(ModalType.SMALL),
              data: {
                subject: 'Driving direction',
                replyTo,
                messageBody: `<p>Please follow the link below to get Driving Directions from ` +
                  `${action.payload.origin} to ${action.payload.destination}:</p>` +
                  `<a href="${drivingDirectionsUrl}">${drivingDirectionsUrl}</a>`
              }
            }
          )
          .afterClosed()
          .pipe(
            filter(Boolean),
            map((emailData: Email) => new SendEmail(emailData))
          );
      }),
    );
  });

  changeTabView$: Observable<any> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.CHANGE_SIDE_PANEL_TAB_VIEW),
      map((tab: ChangeSidePanelTabView) => {
        this.mixpanelService.track('Search - Tab Changed', {
          searchType: tab.payload
        });
      })
    );
  }, {dispatch: false});

  private sendBufferedLayers():
    (source: Observable<[SearchBoardActions.ToggleBoundary, PreferenceGroup]>) => Observable<UpdateMapPreferences> {
    return (source: Observable<[SearchBoardActions.ToggleBoundary, PreferenceGroup]>) => source.pipe(
      map(([action, preferenceGroup]: [SearchBoardActions.ToggleBoundary, PreferenceGroup]) => {
        const preferenceElement = PreferenceUtils.getPreferenceElement(preferenceGroup, action.payload.preferenceElementCode);
        if (preferenceElement) {
          return new UpdateMapPreferences([
            PreferenceUtils.setPreferenceValue(preferenceElement, !action.payload.isSelected)
          ]);
        }
      })
    );
  }

  private getReverseLink(preferenceGroup: PreferenceGroup, propertyInformation: PropertyInformation): Observable<string> {
    return this.mapService.getReverseLinkGrid(preferenceGroup, propertyInformation).pipe(
      map((result: ReverseLinkResponse) => result && result.reverseLinkURL || ''),
      this.openLink('Reverse link error or link does not exist')
    );
  }

  private openLink(message: string) {
    return (source: Observable<string>) => source.pipe(
      tap((link: string) => {
        link ?
          window.open(link, '_blank') :
          this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, message);
      }),
      catchError(() => {
        this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, message);
        return of(void (0));
      })
    );
  }
}
